import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    SHIPPING_SETTINGS_PATH,
    shippingTone,
    isBannerSnoozed,
    snoozeBanner,
} from "../utils/shippingStatus";

/**
 * The shipping setup UI around the dashboard (eventseasyadmin#299), from a
 * shippingStatus() (utils/shippingStatus.js). Each part shows nothing while
 * the state is "unknown". Styling: AdminTheme.css .mtk-ship-*.
 */

const formatDate = (date) => date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const formatDateTime = (date) => date.toLocaleString("en-GB", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
});

const AlertIcon = () => (
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 7v6M12 17h.01" />
    </svg>
);

const ClockIcon = () => (
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
);

const TruckIcon = () => (
    <svg aria-hidden="true" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" /><circle cx="7" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" />
    </svg>
);

/** "● Shipping: Not set up" / "● Shipping: Delhivery · Failed" / "● Shipping: Delhivery", next to the store link */
export function ShippingPill({ status }) {
    const { t } = useTranslation();
    const tone = shippingTone(status.state);
    if (!tone) return null;
    const text = status.state === "ready"
        ? t("shipping.pill.ready", { provider: status.providerLabel })
        : status.state === "failed"
            ? t("shipping.pill.failed", { provider: status.providerLabel })
            : t("shipping.pill.notSetUp");
    return (
        <Link to={SHIPPING_SETTINGS_PATH} className={`mtk-ship-pill mtk-ship--${tone}`} data-testid="shipping-pill">
            <span className="dot" aria-hidden="true" />
            {text}
        </Link>
    );
}

/** SET UP (red when orders are refused, amber when they aren't yet) / FIX next to the sidebar's Shipping Settings */
export function ShippingNavBadge({ status }) {
    const { t } = useTranslation();
    if (!["not_set_up", "optional", "grace", "failed"].includes(status.state)) return null;
    return (
        <span className={`mtk-ship-badge mtk-ship--${shippingTone(status.state)}`} data-testid="shipping-nav-badge">
            {status.state === "failed" ? t("shipping.badge.fix") : t("shipping.badge.setUp")}
        </span>
    );
}

/**
 * Under the top bar on every page while shipping isn't set up. A
 * .main-header, so it lines up with the top bar whichever way the sidebar is.
 * Red ("customers can't place orders", "new orders are paused") only when the
 * API says checkout really refuses the store's orders (ordersBlocked);
 * otherwise amber, and "Remind me tomorrow" hides it for a day.
 */
export function ShippingBanner({ status, businessId }) {
    const { t } = useTranslation();
    const [snoozed, setSnoozed] = useState(() => isBannerSnoozed(businessId));

    if (status.state === "grace" || status.state === "optional") {
        if (snoozed) return null;
        const grace = status.state === "grace";
        return (
            <div className="main-header mtk-ship-banner mtk-ship--amber" role="status" data-testid="shipping-banner">
                <span className="mtk-ship-banner__msg">
                    <ClockIcon />
                    <span>
                        <strong>
                            {grace
                                ? t("shipping.banner.graceTitle", { date: formatDate(status.graceUntil) })
                                : t("shipping.banner.optionalTitle")}
                        </strong>
                        <span className="d-block">
                            {grace
                                ? `${t("shipping.banner.graceText")} ${t("shipping.banner.daysLeft", { count: status.daysLeft })}`
                                : t("shipping.banner.optionalText")}
                        </span>
                    </span>
                </span>
                <span className="mtk-ship-banner__actions">
                    <Link to={SHIPPING_SETTINGS_PATH} className="mtk-ship-btn mtk-ship-btn--green">{t("shipping.banner.setUp")}</Link>
                    <button
                        type="button"
                        className="mtk-ship-btn mtk-ship-btn--plain"
                        onClick={() => {
                            snoozeBanner(businessId);
                            setSnoozed(true);
                        }}
                    >
                        {t("shipping.banner.remind")}
                    </button>
                </span>
            </div>
        );
    }

    if (status.state !== "not_set_up" && status.state !== "failed") return null;
    const failed = status.state === "failed";
    // When it failed is only in the seller's own settings; the store info says that it did
    const failedText = status.failedAt
        ? t("shipping.banner.failedText", { provider: status.providerLabel, date: formatDateTime(status.failedAt) })
        : t("shipping.banner.failedTextNoDate", { provider: status.providerLabel });
    return (
        <div className="main-header mtk-ship-banner mtk-ship--red" role="alert" data-testid="shipping-banner">
            <span className="mtk-ship-banner__msg">
                <AlertIcon />
                <span>
                    <strong>
                        {failed
                            ? t(status.ordersBlocked ? "shipping.banner.failedTitle" : "shipping.banner.failedTitleOpen", { provider: status.providerLabel })
                            : t("shipping.banner.notSetUpTitle")}
                    </strong>
                    <span className="d-block">
                        {failed ? failedText : t("shipping.banner.notSetUpText")}
                    </span>
                </span>
            </span>
            <span className="mtk-ship-banner__actions">
                <Link to={SHIPPING_SETTINGS_PATH} className="mtk-ship-btn mtk-ship-btn--red">
                    {failed ? t("shipping.banner.fix") : t("shipping.banner.setUp")}
                </Link>
            </span>
        </div>
    );
}

/**
 * "Connect your courier", the getting-started step on the product page.
 * step / total: where the courier is in the setup checklist.
 */
export function ConnectCourierModal({ step, total, onClose }) {
    const { t } = useTranslation();
    const dialogRef = useRef(null);
    // The latest onClose, so a parent re-render doesn't move focus back to the dialog
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        if (dialogRef.current) dialogRef.current.focus();
        const onKey = (e) => {
            if (e.key === "Escape") closeRef.current();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div className="mtk-ship-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div
                ref={dialogRef}
                className="mtk-ship-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="mtk-ship-modal-title"
                tabIndex={-1}
            >
                <div className="mtk-ship-modal__top">
                    <span>{t("shipping.modal.step", { step, total })}</span>
                    <button type="button" className="mtk-ship-modal__close" aria-label={t("shipping.modal.close")} onClick={onClose}>×</button>
                </div>
                <div className="mtk-ship-modal__body">
                    <div className="mtk-ship-modal__icon"><TruckIcon /></div>
                    <h2 id="mtk-ship-modal-title">{t("shipping.modal.title")}</h2>
                    <p>{t("shipping.modal.text")}</p>
                    <div className="mtk-ship-choices">
                        <Link to={`${SHIPPING_SETTINGS_PATH}?mode=courier`} className="mtk-ship-choice is-primary" onClick={onClose}>
                            <strong>{t("shipping.modal.courierTitle")}</strong>
                            <span>{t("shipping.modal.courierText")}</span>
                        </Link>
                        <Link to={`${SHIPPING_SETTINGS_PATH}?mode=manual`} className="mtk-ship-choice" onClick={onClose}>
                            <strong>{t("shipping.modal.manualTitle")}</strong>
                            <span>{t("shipping.modal.manualText")}</span>
                        </Link>
                    </div>
                </div>
                <div className="mtk-ship-modal__foot">
                    <div className="mtk-ship-dots" aria-hidden="true">
                        {Array.from({ length: total }, (_, i) => (
                            <span key={i} className={i + 1 === step ? "is-current" : ""} />
                        ))}
                    </div>
                    <div className="mtk-ship-modal__actions">
                        <button type="button" className="mtk-ship-btn mtk-ship-btn--plain" onClick={onClose}>{t("shipping.modal.skip")}</button>
                        <Link to={SHIPPING_SETTINGS_PATH} className="mtk-ship-btn mtk-ship-btn--green" onClick={onClose}>
                            {t("shipping.modal.setUp")}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** "Get your store ready to take orders", above the plan on the product page until every step is done */
export function StoreSetupChecklist({ steps, productCount, storeLink }) {
    const { t } = useTranslation();
    const done = steps.filter((s) => s.done).length;
    if (done === steps.length) return null;

    const doneText = {
        products: t("shipping.checklist.productsDone", { count: productCount }),
        payment: t("shipping.checklist.paymentDone"),
        courier: t("shipping.checklist.courierDone"),
        store: storeLink,
    };
    const todoText = {
        products: t("shipping.checklist.productsTodo"),
        payment: t("shipping.checklist.paymentTodo"),
        courier: t("shipping.checklist.courierTodo"),
        store: t("shipping.checklist.storeTodo"),
    };
    const todoLink = { products: "/gift_add", payment: "/create-payment", courier: SHIPPING_SETTINGS_PATH, store: "/webstore_settings" };

    return (
        <section className="mtk-ship-checklist" aria-labelledby="mtk-ship-checklist-title" data-testid="setup-checklist">
            <div className="mtk-ship-checklist__head">
                <h2 id="mtk-ship-checklist-title">{t("shipping.checklist.title")}</h2>
                <span>{t("shipping.checklist.progress", { done, total: steps.length })}</span>
            </div>
            <div className="mtk-ship-progress" aria-hidden="true">
                <div style={{ width: `${(done / steps.length) * 100}%` }} />
            </div>
            <ol>
                {steps.map((s) => (
                    <li key={s.key} className={s.done ? "is-done" : s.key === "courier" ? "is-urgent" : "is-todo"}>
                        <span className="mtk-ship-checklist__label">
                            {s.done && <span aria-hidden="true">✓ </span>}
                            {t(`shipping.checklist.${s.key}`)}
                            {s.done && <span className="sr-only"> ({t("shipping.checklist.doneLabel")})</span>}
                        </span>
                        <span className="mtk-ship-checklist__text">{s.done ? doneText[s.key] : todoText[s.key]}</span>
                        {!s.done && (
                            <Link to={todoLink[s.key]} className={s.key === "courier" ? "mtk-ship-btn mtk-ship-btn--green mtk-ship-btn--sm" : "mtk-ship-checklist__link"}>
                                {t(`shipping.checklist.${s.key}Action`)}
                            </Link>
                        )}
                    </li>
                ))}
            </ol>
        </section>
    );
}

/** "n products have no weight", with a filter to just those products */
export function MissingWeightNotice({ count, filtered, onToggle }) {
    const { t } = useTranslation();
    if (!count) return null;
    return (
        <div className="mtk-ship-notice" role="status" data-testid="missing-weight">
            <span>
                <strong>{t("shipping.weight.missing", { count })}</strong> {t("shipping.weight.why")}
            </span>
            <button type="button" className="mtk-ship-notice__link" onClick={onToggle} aria-pressed={filtered}>
                {filtered ? t("shipping.weight.showAll") : t("shipping.weight.show")}
            </button>
        </div>
    );
}
